import React, { useEffect, useState } from "react";
import oraclePhase27Api from "@/api/oraclePhase27Client";
import ComplianceLock from "@/components/compliance/ComplianceLock";

/**
 * CareLogix™ ORACLE — Crypto Intelligence Panel (Phase 27F).
 *
 * Token Metrics-inspired but original. Shows token score, trend score,
 * liquidity risk, whale activity placeholder, exchange flow placeholder,
 * volatility rating, market cycle badge, watch/neutral/risk-high label.
 */
const DEFAULT_SYMBOLS = ["BTC", "ETH", "SOL", "AVAX", "LINK", "DOT", "MATIC", "OP"];

function ScoreBar({ value, color = "#c5a059" }) {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <div style={S.bar}><div style={{ ...S.barFill, width: `${v}%`, background: `linear-gradient(90deg, ${color}55, ${color})` }} /></div>
  );
}

export default function OracleCryptoIntelligence({
  defaultSymbol = "BTC",
  symbols = DEFAULT_SYMBOLS,
  testid = "oracle-crypto-intel",
}) {
  const [symbol, setSymbol] = useState(defaultSymbol);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setBusy(true); setErr("");
    oraclePhase27Api.cryptoIntel(symbol)
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setErr(e?.message || "crypto_intel_failed"); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [symbol]);

  const ci = data?.crypto;
  const label = ci?.label;

  return (
    <section style={S.shell} data-testid={testid}>
      <header style={S.head}>
        <div>
          <span style={S.tagline}>ORACLE Crypto Intelligence™</span>
          <h3 style={S.title}>₿ Token · {symbol}</h3>
        </div>
        <div style={S.symbolChips} role="tablist" aria-label="Token list">
          {symbols.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSymbol(s)}
              role="tab"
              aria-selected={symbol === s}
              data-testid={`crypto-token-${s}`}
              style={{ ...S.symBtn, ...(symbol === s ? S.symBtnActive : {}) }}
            >{s}</button>
          ))}
        </div>
      </header>

      {err && <div style={S.err}>⚠ {err}</div>}
      {busy && !data && <div style={S.empty}>Loading…</div>}
      {ci && (
        <>
          <div style={S.headerRow}>
            <div style={S.bigScore} data-testid={`${testid}-token-score`}>{ci.token_score}<span style={S.scoreSlash}>/100</span></div>
            <div style={S.headerMeta}>
              <span data-testid={`${testid}-cycle`} style={{ ...S.cyclePill, color: ci.market_cycle.color, borderColor: ci.market_cycle.color, background: `${ci.market_cycle.color}1a` }}>
                {ci.market_cycle.label}
              </span>
              <span data-testid={`${testid}-label`} style={{ ...S.cyclePill, color: label.color, borderColor: label.color, background: `${label.color}1a` }}>
                {label.label}
              </span>
            </div>
          </div>

          <div style={S.grid}>
            <div style={S.card}>
              <strong style={S.cardTitle}>Token Score</strong>
              <span style={S.cardScore}>{ci.token_score}</span>
              <ScoreBar value={ci.token_score} color="#c5a059" />
              <p style={S.cardNote}>Composite from sub-factor stack.</p>
            </div>
            <div style={S.card}>
              <strong style={S.cardTitle}>Liquidity Risk</strong>
              <span style={{ ...S.cardScore, color: "#fca5a5" }}>{ci.liquidity_risk}</span>
              <ScoreBar value={ci.liquidity_risk} color="#ef4444" />
              <p style={S.cardNote}>Lower is safer.</p>
            </div>
            <div style={S.card}>
              <strong style={S.cardTitle}>Volatility Rating</strong>
              <span style={{ ...S.cardScore, color: "#fcd34d" }}>{ci.volatility_rating}</span>
              <ScoreBar value={ci.volatility_rating} color="#fcd34d" />
              <p style={S.cardNote}>Realized vol percentile.</p>
            </div>
            <div style={S.card}>
              <strong style={S.cardTitle}>Whale Activity <em style={S.placeholderTag}>placeholder</em></strong>
              <span style={{ ...S.cardScore, color: "#67e8f9" }}>{ci.whale_activity.score}</span>
              <ScoreBar value={ci.whale_activity.score} color="#67e8f9" />
              <p style={S.cardNote}>Source: {ci.whale_activity.source}</p>
            </div>
            <div style={S.card}>
              <strong style={S.cardTitle}>Exchange Flow <em style={S.placeholderTag}>placeholder</em></strong>
              <span style={{ ...S.cardScore, color: "#a78bfa" }}>{ci.exchange_flow.score}</span>
              <ScoreBar value={ci.exchange_flow.score} color="#a78bfa" />
              <p style={S.cardNote}>Source: {ci.exchange_flow.source}</p>
            </div>
            <div style={S.card}>
              <strong style={S.cardTitle}>Confidence · Uncertainty</strong>
              <span style={S.cardScore}>{data.meta?.mean_confidence}% / {data.meta?.mean_uncertainty}%</span>
              <ScoreBar value={data.meta?.mean_confidence} color="#10b981" />
              <p style={S.cardNote}>Higher confidence + lower uncertainty preferred.</p>
            </div>
          </div>

          <p style={S.disclaimer}>{ci.on_chain_disclaimer}</p>
        </>
      )}

      <ComplianceLock variant="inline" short policy="v27" testid={`${testid}-compliance`} />
    </section>
  );
}

const S = {
  shell: { background: "rgba(11,15,20,0.94)", border: "1px solid rgba(197,160,89,0.22)", borderRadius: 14, padding: 14, color: "#e2e8f0", display: "flex", flexDirection: "column", gap: 10, fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" },
  tagline: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.18em" },
  title: { color: "#fde68a", margin: 0, fontSize: 17 },
  symbolChips: { display: "flex", flexWrap: "wrap", gap: 4 },
  symBtn: { appearance: "none", background: "transparent", border: "1px solid rgba(255,255,255,0.10)", color: "#94a3b8", padding: "3px 8px", borderRadius: 6, cursor: "pointer", fontFamily: "'Space Mono', monospace", fontSize: 10.5, fontWeight: 800 },
  symBtnActive: { background: "rgba(197,160,89,0.15)", borderColor: "#c5a059", color: "#c5a059" },
  err: { padding: "6px 9px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", borderRadius: 8, color: "#fecaca", fontSize: 12 },
  empty: { textAlign: "center", padding: 14, color: "#94a3b8", fontFamily: "'Space Mono', monospace" },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 },
  bigScore: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontWeight: 900, fontSize: 38, letterSpacing: "-0.03em" },
  scoreSlash: { fontSize: 14, color: "#94a3b8", marginLeft: 4 },
  headerMeta: { display: "flex", gap: 8, flexWrap: "wrap" },
  cyclePill: { fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.08em", fontWeight: 800, padding: "4px 10px", border: "1px solid", borderRadius: 999 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 },
  card: { background: "#040608", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 10, padding: 10, display: "flex", flexDirection: "column", gap: 4 },
  cardTitle: { color: "#fde68a", fontSize: 12 },
  cardScore: { color: "#c5a059", fontFamily: "'Space Mono', monospace", fontWeight: 800, fontSize: 18 },
  bar: { height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden" },
  barFill: { height: "100%", borderRadius: 4 },
  cardNote: { color: "#94a3b8", margin: 0, fontSize: 11, fontStyle: "italic" },
  placeholderTag: { color: "#fcd34d", marginLeft: 4, fontSize: 9.5, fontStyle: "normal", fontWeight: 700, letterSpacing: "0.06em" },
  disclaimer: { color: "#94a3b8", fontStyle: "italic", fontSize: 11, lineHeight: 1.4, margin: 0 },
};
